import { useCallback, useState } from "react";
import useSWR from "swr";

import {
  createVending,
  deleteVending,
  listVendings,
  updateVending,
  VENDINGS_KEY,
  type UpdateVendingPayload,
  type Vending,
  type VendingData,
} from "@/features/vending/api/vendingApi";
import { revalidate, toErrorMessage } from "@/lib/swr";

const EMPTY: Vending[] = [];

/** A sale moves the merchant stock and the account it was cashed into. */
const TOUCHED_CACHES = [VENDINGS_KEY, "product-stocks/", "accounts/"];

export function useVendings() {
  const { data, error, isLoading, mutate } = useSWR(VENDINGS_KEY, listVendings);

  return {
    vendings: data ?? EMPTY,
    isLoading,
    error: toErrorMessage(error),
    refresh: mutate,
  };
}

export function useCreateVending() {
  const { mutate } = useSWR(VENDINGS_KEY, listVendings);
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const create = useCallback(
    async (payload: VendingData) => {
      setIsCreating(true);
      setError(null);
      try {
        const created = await createVending(payload);
        await mutate();
        return created;
      } catch (e) {
        setError(toErrorMessage(e));
        throw e;
      } finally {
        setIsCreating(false);
      }
    },
    [mutate],
  );

  return { create, isCreating, error };
}

export function useUpdateVending() {
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const update = useCallback(async (id: string, payload: UpdateVendingPayload) => {
    setIsUpdating(true);
    setError(null);
    try {
      const updated = await updateVending(id, payload);
      await revalidate(...TOUCHED_CACHES);
      return updated;
    } catch (e) {
      setError(toErrorMessage(e));
      throw e;
    } finally {
      setIsUpdating(false);
    }
  }, []);

  return { update, isUpdating, error };
}

export function useDeleteVending() {
  const [isDeleting, setIsDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const remove = useCallback(async (id: string) => {
    setIsDeleting(true);
    setError(null);
    try {
      const deleted = await deleteVending(id);
      await revalidate(...TOUCHED_CACHES);
      return deleted;
    } catch (e) {
      setError(toErrorMessage(e));
      throw e;
    } finally {
      setIsDeleting(false);
    }
  }, []);

  return { remove, isDeleting, error };
}
